/**
 * The share-management entries of the files-mode toolbar (plan Task 7.16; band 500–599, beside the transfer entries
 * of 500–520): Properties 530, Snapshots… 540, Get SAS… 550, then under "more" Quota… 560, Mount command… 570 and
 * Delete share… 580 (at the share root only). Each is disabled with its reason — read-only, the §3.7 attachment rule,
 * the credential gate (`fileActionReason` over `azureStorageFileCoverage.ts`), a snapshot — and opens its dialog
 * through `openShareDialog` with `source: 'toolbar'` (Get SAS… opens S3's SAS dialog). The files tab passes
 * `capabilities: { tabId, protocol }` (the protocol main resolved for the share, S7-08).
 */
import { createElement, type ReactNode } from 'react';
import { Camera, Gauge, Info, KeyRound, Terminal, Trash2 } from 'lucide-react';
import { ReasonButton } from '../../../../components/ui/ReasonButton';
import { SNAPSHOT_READ_ONLY_REASON } from '../../../../core/engines/azureStorageFileCoverage';
import type { FileServiceRef, FileShareOperation, FileShareProtocolState, StorageResourceRef } from '../../../../types/azureStorage';
import { useStorageDialogStore } from '../properties/dialogs/storageDialogStore';
import { sasTargetProblem } from '../properties/sasTargets';
import { registerStorageToolbarExtension, TOOLBAR_OVERFLOW_PRIORITY, type StorageToolbarContext, type StorageToolbarExtension } from '../toolbar/toolbarExtensions';
import { StorageToolbarMenuItem } from '../toolbar/StorageToolbarMenuItem';
import { useShareRefusal } from './dialogs/shareGate';
import { openShareDialog, type ShareDialogKind } from './fileDialogStore';

type ShareRoot = { share: string; snapshot?: string };
type Entry = { id: string; placement: 'primary' | 'more'; order: number; testId: string; label: string; icon: typeof Info; visible?: (ctx: StorageToolbarContext) => boolean; overflowPriority?: number };

const isFileRef = (ref: StorageResourceRef): ref is FileServiceRef => ref.kind === 'share' || ref.kind === 'directory' || ref.kind === 'file';
const shareOf = (ctx: StorageToolbarContext): ShareRoot | undefined => (isFileRef(ctx.root) ? { share: ctx.root.share, snapshot: ctx.root.snapshot || undefined } : undefined);
const protocolOf = (ctx: StorageToolbarContext): FileShareProtocolState => (ctx.capabilities.protocol === 'smb' || ctx.capabilities.protocol === 'nfs' ? ctx.capabilities.protocol : 'unknown');
const tabIdOf = (ctx: StorageToolbarContext): string | undefined => (typeof ctx.capabilities.tabId === 'string' ? ctx.capabilities.tabId : undefined);
/** The share root in whichever shape the tab holds it: the share ref, or the root directory (`path: ''`). */
const atShareRoot = (ref: StorageResourceRef): boolean => ref.kind === 'share' || (ref.kind === 'directory' && ref.path === '');
const asShare = (ref: FileServiceRef): FileServiceRef => (ref.kind === 'directory' && ref.path === '' ? { kind: 'share', share: ref.share, ...(ref.snapshot ? { snapshot: ref.snapshot } : {}) } : ref);

/** What one entry does in this context: its disabled reason, or its action. */
function plan(id: string, ctx: StorageToolbarContext, refuse: (op: FileShareOperation, snapshot?: string) => string | undefined): { reason?: string; run?: () => void } {
  const root = shareOf(ctx);
  if (!root) return { reason: 'This location is not a file share.' };
  const protocol = protocolOf(ctx);
  const live = { kind: 'share', share: root.share } as const;
  const open = (kind: ShareDialogKind, resource: FileServiceRef) => () => openShareDialog({ kind, connectionId: ctx.connectionId, resource, source: 'toolbar', protocol, ...(tabIdOf(ctx) ? { tabId: tabIdOf(ctx) } : {}) });
  const guarded = (op: FileShareOperation, kind: ShareDialogKind, snapshot?: string) => {
    const reason = refuse(op, snapshot);
    return reason ? { reason } : { run: open(kind, live) };
  };
  switch (id) {
    case 's7-properties': {
      if (ctx.selection.length > 1) return { reason: 'Select one item to see its properties.' };
      const target = ctx.selection.length === 1 ? ctx.selection[0] : ctx.resource;
      if (!isFileRef(target)) return { reason: 'Properties are not available for this item.' };
      const ref = asShare(target);
      const reason = refuse(ref.kind === 'share' ? 'share-properties' : 'item-properties', ref.snapshot);
      return reason ? { reason } : { run: open('properties', ref) };
    }
    case 's7-snapshots': return guarded('snapshot-list', 'share-snapshots');
    case 's7-quota': return guarded('set-quota', 'share-quota', root.snapshot);
    case 's7-mount': return guarded('mount-command', 'mount-command');
    case 's7-delete-share': return guarded('delete-share', 'delete-share', root.snapshot);
    case 's7-sas': {
      const target: FileServiceRef = ctx.selection.length === 1 && isFileRef(ctx.selection[0]) ? ctx.selection[0] : { kind: 'share', share: root.share };
      if (root.snapshot || target.snapshot) return { reason: SNAPSHOT_READ_ONLY_REASON };
      const reason = sasTargetProblem(target) ?? refuse('service-sas');
      return reason ? { reason } : { run: () => useStorageDialogStore.getState().openDialog({ kind: 'sas', connectionId: ctx.connectionId, resource: target, source: 'toolbar' }) };
    }
    default: return { reason: 'Unknown action.' };
  }
}

/** `asMenuItem`: a primary entry that moved into More because the action row ran out of width (C1 R5 / N2). */
function ShareToolbarButton({ entry, ctx, asMenuItem = false }: { entry: Entry; ctx: StorageToolbarContext; asMenuItem?: boolean }) {
  const refuse = useShareRefusal(ctx.connectionId, ctx.readOnly);
  const protocol = protocolOf(ctx);
  // Review C1-02: a refused listing does not refuse the share's Properties (share administration, authorized apart from
  // file-data access); the dialog attempts its own read.
  const { reason, run } = plan(entry.id, ctx, (op, snapshot) => refuse(op, { protocol, snapshot }));
  const Icon = entry.icon;
  // UI/UX fix round A (A4): a More entry is a real menu item (keyboard reachable, closes before its dialog opens).
  if (entry.placement === 'more' || asMenuItem) {
    return <StorageToolbarMenuItem icon={Icon} label={entry.label} destructive={entry.id === 's7-delete-share'} reason={run ? undefined : reason ?? entry.label} onSelect={() => run?.()} testId={entry.testId} />;
  }
  return (
    <ReasonButton
      variant="outline" size="xs" className="gap-1"
      reason={run ? undefined : reason ?? entry.label} title={entry.label} data-testid={entry.testId} onClick={() => run?.()}
    >
      <Icon className="h-3.5 w-3.5" /> {entry.label}
    </ReasonButton>
  );
}

const ENTRIES: Entry[] = [
  // UI/UX fix round A (U7 / U36): Properties in the one Properties position; Get SAS… in More, as on a blob container;
  // a single attached file has no snapshots of its own and no toolbar SAS (the attachment is the SAS).
  { id: 's7-properties', placement: 'primary', order: 200, testId: 'storage-toolbar-share-properties', label: 'Properties', icon: Info, overflowPriority: TOOLBAR_OVERFLOW_PRIORITY.properties },
  { id: 's7-snapshots', placement: 'primary', order: 540, testId: 'storage-toolbar-share-snapshots', label: 'Snapshots…', icon: Camera, visible: (ctx) => ctx.root.kind !== 'file', overflowPriority: TOOLBAR_OVERFLOW_PRIORITY.snapshots },
  { id: 's7-sas', placement: 'more', order: 550, testId: 'storage-toolbar-share-sas', label: 'Get SAS…', icon: KeyRound, visible: (ctx) => ctx.root.kind !== 'file' },
  { id: 's7-quota', placement: 'more', order: 560, testId: 'storage-toolbar-share-quota', label: 'Quota…', icon: Gauge },
  { id: 's7-mount', placement: 'more', order: 570, testId: 'storage-toolbar-share-mount', label: 'Mount command…', icon: Terminal },
  { id: 's7-delete-share', placement: 'more', order: 580, testId: 'storage-toolbar-share-delete-share', label: 'Delete share…', icon: Trash2, visible: (ctx) => atShareRoot(ctx.resource) },
];

type Register = (extension: StorageToolbarExtension) => void;
const registered = new WeakSet<Register>();

export function registerStorageShareToolbarExtensions(register: Register = registerStorageToolbarExtension): void {
  if (registered.has(register)) return;
  registered.add(register);
  for (const entry of ENTRIES) {
    register({
      id: entry.id, placement: entry.placement, order: entry.order, ...(entry.id === 's7-delete-share' ? { destructive: true } : {}),
      isVisible: (ctx) => ctx.mode === 'files' && (entry.visible?.(ctx) ?? true),
      render: (ctx): ReactNode => createElement(ShareToolbarButton, { entry, ctx }),
      ...(entry.overflowPriority !== undefined ? { overflow: { priority: entry.overflowPriority, renderMenuItem: (ctx: StorageToolbarContext): ReactNode => createElement(ShareToolbarButton, { entry, ctx, asMenuItem: true }) } } : {}),
    });
  }
}
